
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, Eye, EyeOff } from 'lucide-react';
import { 
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Faq } from '@/lib/supabase/products';

interface FaqItemProps {
  faq: Faq;
  updateFaq: (id: string, field: keyof Faq, value: any) => void;
  removeFaq: (id: string) => void;
  toggleVisibility: (id: string) => void;
}

export const FaqItem = ({ 
  faq, 
  updateFaq, 
  removeFaq,
  toggleVisibility 
}: FaqItemProps) => {
  return (
    <AccordionItem 
      key={faq.id} 
      value={faq.id} 
      className="border rounded-md dark:border-gray-700 dark:bg-[#1a1a2e] overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-2">
        <AccordionTrigger className="hover:bg-gray-50 dark:hover:bg-[#252A41] flex-1 py-2 transition-all">
          <div className="flex items-center space-x-2">
            <span className="text-gray-700 dark:text-gray-200">{faq.question || "Nouvelle question"}</span>
          </div>
        </AccordionTrigger>
        <div className="flex items-center gap-2 ml-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              toggleVisibility(faq.id);
            }}
            className={faq.visible === false ? "text-gray-400" : "text-green-600 dark:text-green-500"}
            title={faq.visible === false ? "FAQ masquée" : "FAQ visible"}
          >
            {faq.visible === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      <AccordionContent className="px-4 py-3 space-y-4 bg-[#151521]">
        <div className="space-y-2">
          <Label htmlFor={`question-${faq.id}`} className="text-gray-700 dark:text-gray-300">Question</Label>
          <Input
            id={`question-${faq.id}`}
            value={faq.question}
            onChange={(e) => updateFaq(faq.id, "question", e.target.value)}
            placeholder="Ex: Comment utiliser ce produit?"
            className="bg-white dark:bg-[#151521] border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`answer-${faq.id}`} className="text-gray-700 dark:text-gray-300">Réponse</Label>
          <Textarea
            id={`answer-${faq.id}`}
            value={faq.answer}
            onChange={(e) => updateFaq(faq.id, "answer", e.target.value)}
            placeholder="Ex: Voici les instructions pour utiliser ce produit..."
            className="bg-white dark:bg-[#151521] border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 min-h-[80px]"
          />
        </div>
        
        <div className="flex items-center space-x-2 pt-2">
          <Checkbox 
            id={`visible-${faq.id}`}
            checked={faq.visible !== false}
            onCheckedChange={(checked) => updateFaq(faq.id, "visible", checked)}
            className="text-blue-600 dark:text-blue-500"
          />
          <Label 
            htmlFor={`visible-${faq.id}`} 
            className="cursor-pointer text-gray-700 dark:text-gray-300"
          >
            Afficher cette FAQ
          </Label>
        </div>
        
        <div className="pt-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => removeFaq(faq.id)}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
